/* eslint-disable no-use-before-define */
import { DiContainer, Injectable, InjectByToken } from '@jack-henry/frontend-utils/di';
import { isNonEmptyString, WindowService } from '@treasury/utils';
import {
    AppType,
    AppTypeToken,
    BACKOFFICE_API,
    BuildType,
    BuildTypeToken,
    CHANNEL_API,
    Environment,
    InjectedWindow,
} from './configuration.types';

let vanityUrlToIdMap = new Map<string, string>();
try {
    vanityUrlToIdMap = require('./vanity-url-map').vanityUrlToIdMap;
} catch (error) {
    console.warn(
        'Could not load vanity URL map because require was not defined. Defaulting to empty map.'
    );
}

/**
 * Service exposing information used for bootstrapping or
 * otherwise configuring channel and BO applications.
 */
@Injectable()
export class ConfigurationService {
    // eslint-disable-next-line no-useless-constructor
    public constructor(
        @InjectByToken(AppTypeToken) public readonly app: AppType,
        @InjectByToken(BuildTypeToken) public readonly buildType: BuildType,
        private readonly window: WindowService
    ) {
        if (app === AppType.Unknown) {
            throw new Error('Could not determine application type.');
        }
    }

    public readonly uisRedirectUrl = this.generateSsoCallbackUrl;

    /**
     * The API path used when the client is deployed together with the API on the same domain.
     */
    private readonly apiPath = this.app === AppType.Channel ? CHANNEL_API : BACKOFFICE_API;

    public get institutionId() {
        const path = this.window.location.pathname.split('/').filter(isNonEmptyString);
        if (!path) throw new Error('Incomplete path. Unable to determine institutionId.');
        if (this.app === AppType.Channel) return path[0];
        if (this.app === AppType.Pwa) return path[1];
        return '';
    }

    /**
     * The root URL prepended to all calls made to API endpoints.
     */
    public get apiRoot() {
        const { apiRoot } = this.injectedConfig;

        return isNonEmptyString(apiRoot) ? apiRoot : `${this.baseUrl}/${this.apiPath}`;
    }

    public get analyticsToken() {
        return this.injectedConfig.analyticsToken;
    }

    public get environment() {
        if (this.injectedMetadata.isLocal) {
            return Environment.Local;
        }

        const { hostname } = this.window.location;
        if (hostname) {
            const vanityUrls = Array.from((vanityUrlToIdMap as Map<string, string>).keys());
            if (vanityUrls.some(url => hostname === url)) {
                return Environment.Production;
            }

            switch (hostname) {
                case '127.0.0.1':
                case 'localhost':
                    return Environment.Local;
                case 'treasury.dev.jackhenry.com':
                case 'treasurybackoffice.dev.jackhenry.com':
                    return Environment.Dev;
                case 'treasury.stg.jackhenry.com':
                case 'treasurybackoffice.stg.jackhenry.com':
                    return Environment.Staging;
                case 'treasury.jackhenry.com':
                case 'treasurybackoffice.jackhenry.com':
                    return Environment.Production;
                default:
                    return Environment.Unknown;
            }
        }

        return Environment.Unknown;
    }

    /**
     * The client-side ID token used by the feature flag SDK.
     */
    public get ffToken() {
        // maps an `Environment` to a LD Client-side ID
        // https://docs.launchdarkly.com/sdk/concepts/client-side-server-side#client-side-id
        switch (this.environment) {
            case Environment.Production:
                return '6633bf61801694108e04e3c2';
            case Environment.Dev:
                return '6633c04c1a652f0fc78187eb';
            case Environment.Local:
                return '665e22a09e41bb108d27a538';
            case Environment.Staging:
                return '66858f281005aa0f93eacc22';
            default:
                throw new Error('Could not retrieve LD SDK token.');
        }
    }

    public get loggingToken() {
        return this.injectedConfig.loggingToken;
    }

    public get remoteDepositToken() {
        // maps an `Environment` to a Remote Deposit SDK token
        switch (this.environment) {
            case Environment.Production:
            case Environment.Dev:
            case Environment.Staging:
                // case Environment.Local:
                return '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';
            case Environment.Local:
                return 'eyJzaWduYXR1cmUiOiJRd0x2QzdlNEZ1eHp0UVg5YkwrM0dZR21KK1pQQmY4YUJsM3E4amtLSitlUzNBd3ZYeFhkaE9jcE9FLzY1Q1o5MXJuUlcrNmFZNWtYRkFHNFVzOWVOSlpUYVlrQnZzaXZLZjF5U1E3T0ZFUFdsMEdoNU44NW9uV2JVMlErYkJKaFY2bXNlb0dKM3dtT01Xc1hCNjlBb240RTE3TGRHdzNJMmJiWUgxZkxNOE9sVjhtc3U3WHlnUnhkcVBrMDROZlp0cFBGR0h2bEVWbjF5TEZzRVZVUmZCaXhZM1BTK0g5YzU3SnAxVWFHWDJFdU05S3g0c05rbkc5SkVRZEF3cGVnSmtKODV2QTdua1prUDNDbFdxaTlZSW5Md2pmQ0VVSHFtWnl3L0gxbmFTeUxKUURoNHdGbkJmbFZieGpXblFPV1c5djBJREQva29aVFVIR1V0di9TOUE9PSIsInNpZ25lZCI6eyJleHBpcnkiOiIyMDI2LTA3LTMxIiwiZmVhdHVyZXMiOlsiZGVwb3NpdCIsImVtIl0sImFwcGxpY2F0aW9uSWRlbnRpZmllciI6eyJ3ZWIiOlsiMTAuMC4xLjE1IiwiMTkyLjE2OC4xLjE1Il19LCJncG8iOiI0NDciLCJvcmdhbml6YXRpb24iOiJNaXRlayBTeXN0ZW1zIEluYy4iLCJ2ZXJzaW9uIjoiMS4wIn19';
            default:
                throw new Error('Could not retrieve RDC SDK token.');
        }
    }

    public get version() {
        return this.injectedMetadata.version;
    }

    private get baseUrl() {
        const { protocol, hostname, port } = window.location;
        let url = `${protocol}//${hostname}`;

        if (port) {
            url += `:${port}`;
        }

        return url;
    }

    private get generateSsoCallbackUrl() {
        let { host } = window.location;
        if (this.app === AppType.Pwa) host = `${host}/pwa`;
        return `${window.location.protocol}//${host}/${this.institutionId}/login/sso`;
    }

    private get injectedConfig() {
        return (window as unknown as InjectedWindow).config ?? {};
    }

    private get injectedMetadata() {
        return (window as unknown as InjectedWindow).treasury ?? {};
    }

    /**
     * Convenience method for locations where DI isn't available.
     */
    public static async getInstance() {
        return (await DiContainer.getInstance()).get(ConfigurationService);
    }
}

const buildType = BuildTypeToken.getValue() || BuildType.Unknown;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const channelApiRoot = new ConfigurationService(
    AppType.Channel,
    buildType,
    new WindowService()
).apiRoot;

/**
 * @deprecated Prefer referencing `apiRoot` through the configuration service.
 */
export const boApiRoot = new ConfigurationService(
    AppType.BackOffice,
    buildType,
    new WindowService()
).apiRoot;
